import React from "react";
import {
  Typography,
  Button,
  Box,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link } from "react-router-dom";

const Pricing = () => {
  return (
    <Box
      sx={{
        maxWidth: 1000,
        mx: "auto",
        px: 4,
        py: 8,
      }}
    >
      {/* Pricing Summary */}
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
          }}
        >
          Simple, pay-as-you-get-paid pricing
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mb: 4,
            color: "text.secondary",
            fontWeight: 400,
            fontSize: { xs: "1rem", sm: "1.1rem" },
          }}
        >
          We take a small percentage per transaction — Stripe fees included —
          and lower that percentage as you grow.
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          sx={{ mb: 4 }}
        >
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ No monthly platform fee
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ No setup fee
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ No contracts
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ Automatic payment enforcement included
          </Typography>
        </Stack>
      </Box>

      {/* Marginal Tiered Pricing */}
      <Paper elevation={2} sx={{ p: 4, mb: 6 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 700,
            mb: 1,
            fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
          }}
        >
          Marginal Tiered Pricing
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
          Your rate is calculated per transaction based on your last 30 days of
          revenue.
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  Monthly Coaching Revenue
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Platform Fee
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>$0 – $3,000</TableCell>
                <TableCell align="right">5%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>$3,001 – $8,000</TableCell>
                <TableCell align="right">4%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>$8,001+</TableCell>
                <TableCell align="right">3.5%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Stack spacing={1} sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">Stripe processing included</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">
              Percentage drops automatically as you scale
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">
              No manual upgrades or plan changes
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* How marginal pricing works */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: 2,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          How marginal pricing works
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
          You don't "jump" tiers — each payment is split fairly.
        </Typography>

        <Paper elevation={1} sx={{ p: 3, bgcolor: "grey.50" }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
            Example:
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            You've done $2,900 in the last 30 days.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            A client pays $200.
          </Typography>

          <Box sx={{ pl: 2, borderLeft: 2, borderColor: "primary.main" }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              First $100 → finishes the 5% tier → $5
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Remaining $100 → enters the 4% tier → $4
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, mt: 2 }}>
              Total fee: $9
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Effective rate: 4.5%
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ mt: 3, color: "text.secondary" }}>
            No overcharging. No waiting until next month.
          </Typography>
        </Paper>
      </Box>

      {/* Cost comparison */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: 4,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          Cost comparison
        </Typography>

        <Stack spacing={4}>
          {/* Example 1 */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Example: 25 clients paying $200/month
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              Monthly revenue: $5,000
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 3,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Sheets + Zelle
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">$0 in software fees</Typography>
                  <Typography variant="body2">Manual tracking</Typography>
                  <Typography variant="body2">Manual enforcement</Typography>
                  <Typography variant="body2">
                    Missed or late payments
                  </Typography>
                  <Typography variant="body2">Admin time every week</Typography>
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Generic Training App
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Platform fee: $70/month
                  </Typography>
                  <Typography variant="body2">
                    Payment processing: $162.50
                  </Typography>
                  <Typography variant="body2">
                    (3% + $0.50 per transaction)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                    Total: $232.50/month
                  </Typography>
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Trainer App
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    First $3,000 @ 5% → $150
                  </Typography>
                  <Typography variant="body2">
                    Remaining $2,000 @ 4% → $80
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                    Total: $230/month
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mt: 1 }}
                  >
                    One missed payment already costs more than that.
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Paper>

          {/* Example 2 */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Example: 40 clients paying $200/month
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              Monthly revenue: $8,000
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 3,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Sheets + Zelle
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">$0 in software fees</Typography>
                  <Typography variant="body2">Manual tracking</Typography>
                  <Typography variant="body2">Manual enforcement</Typography>
                  <Typography variant="body2">
                    Missed or late payments
                  </Typography>
                  <Typography variant="body2">Admin time every week</Typography>
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Generic Training App
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Platform fee: $120/month
                  </Typography>
                  <Typography variant="body2">
                    Payment processing: $260
                  </Typography>
                  <Typography variant="body2">
                    (3% + $0.50 per transaction)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                    Total: $380/month
                  </Typography>
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Trainer App
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    First $3,000 @ 5% → $150
                  </Typography>
                  <Typography variant="body2">
                    Next $5,000 @ 4% → $200
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                    Total: $350/month
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                    Effective rate: 4.375%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mt: 1 }}
                  ></Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Reduce admin time and improve client retention. Stop chasing
                    payments.
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Paper>

          {/* Example 3 */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Example: 70 clients paying $200/month
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              Monthly revenue: $14,000
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 3,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Sheets + Zelle
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">$0 in software fees</Typography>
                  <Typography variant="body2">Manual tracking</Typography>
                  <Typography variant="body2">Manual enforcement</Typography>
                  <Typography variant="body2">
                    Missed or late payments
                  </Typography>
                  <Typography variant="body2">Admin time every week</Typography>
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Generic Training App
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Platform fee: $170/month
                  </Typography>
                  <Typography variant="body2">
                    Payment processing: $455
                  </Typography>
                  <Typography variant="body2">
                    (3% + $0.50 per transaction)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                    Total: $625/month
                  </Typography>
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Trainer App
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    First $3,000 @ 5% → $150
                  </Typography>
                  <Typography variant="body2">
                    Next $5,000 @ 4% → $200
                  </Typography>
                  <Typography variant="body2">
                    Remaining $6,000 @ 3.5% → $210
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                    Total: $560/month
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                    Effective rate: 4%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mt: 1 }}
                  >
                    Don't pay the same rate for all transactions.
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Other platforms' costs rise even if you're not utilizing
                    full features.
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Paper>
        </Stack>
      </Box>

      {/* Pricing Explanation */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: 2,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          Pricing Explanation
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
          We charge a percentage instead of a flat monthly fee because it keeps
          things fair.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
          When you're just starting, you don't pay upfront. When you scale, we
          lower our cut.
        </Typography>

        <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
          Our fee includes:
        </Typography>
        <Stack spacing={1} sx={{ mb: 3, pl: 2 }}>
          <Typography variant="body2">• Payment processing (Stripe)</Typography>
          <Typography variant="body2">
            • Programming and workout history
          </Typography>
          <Typography variant="body2">
            • Program and template creation
          </Typography>
          <Typography variant="body2">• Custom content uploads</Typography>
          <Typography variant="body2">• Messaging</Typography>
          <Typography variant="body2">• Workout importer</Typography>
          <Typography variant="body2">
            • Client access control when payments stop
          </Typography>
          <Typography variant="body2">
            • Platform updates and support
          </Typography>
        </Stack>
      </Box>

      {/* CTA */}
      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="contained"
          component={Link}
          to="/coaching"
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          Start coaching with Trainer App
        </Button>
      </Box>
    </Box>
  );
};

export default Pricing;
